import { useEffect, useState } from 'react';
import Holidays from 'date-holidays';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { ToastService } from 'services/toast.service';
import { NavigationService } from '../services/navigation.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { AbilityService } from '../services/ability.service';

import { Client, RootState, Site, UserOfAbilityLinked, UserFilter, i18n, Option, CommonGlobalService, MultiSelect, LoggerService } from '@umanforge/common';
import { PostAbility } from 'interfaces/api_ability.interfaces';
import { PostClient } from 'interfaces/api_client.interfaces';
import { PostSite } from 'interfaces/api_site.interfaces';

import { DEFAULT_POST_CLIENT, getCreateClient } from '../constants/client.constants';
import { DEFAULT_POST_SITE, getCreateSite } from '../constants/site.constants';
import { DEFAULT_POST_ABILITY } from '../constants/ability.constants';
import { ROUTES } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { ColorPicker } from '../components/inputs/color_picker.component';
import { ExpirationAbility } from '../components/users/expiration_ability.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { UsersIcon } from '../components/icons/users_icon.component';
import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { AbilitiesIcon } from '../components/icons/abilities_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { PlusIcon } from '../components/icons/plus_icon.component';

const toastService: ToastService = new ToastService( 'Create Ability' );

type PredefinedAuthorization = {
  id: string;
  name: string;
  category: string;
  country?: string
};

export function CreateAbilityView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const logger: LoggerService = new LoggerService( 'CreateAbilityView' );

  const jsonAuthorizationsDir = 'authorization';
  const jsonAuthorizationsFilename = 'authorization.json';

  const createNewClient: Client = getCreateClient( t );
  const createNewSite: Site = getCreateSite( t );

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ usePredefinedAuthorizations, setUsePredefinedAuthorizations ] = useState<boolean>( false );
  const [ allPredefinedAuthorizations, setAllPredefinedAuthorizations ] = useState<PredefinedAuthorization[]>( [] );
  const [ authorizationCategories, setAuthorizationCategories ] = useState<string[]>( [] );
  const [ selectedAuthorizationCategory, setSelectedAuthorizationCategory ] = useState<string>( '' );
  const [ predefinedAuthorizationsOptions, setPredefinedAuthorizationsOptions ] = useState<Option<PredefinedAuthorization>[]>( [] );
  const [ selectedPredefinedAuthorizations, setSelectedPredefinedAuthorizations ] = useState<Option<PredefinedAuthorization>[]>( [] );
  const [ selectedCountry, setSelectedCountry ] = useState<string>( '' );
  const [ countryOptions, setCountryOptions ] = useState<{ label: string; value: string }[]>( [] );

  const [ postAbility, setPostAbility ] = useState<PostAbility>( structuredClone( DEFAULT_POST_ABILITY ) );
  const [ postSite, setPostSite ] = useState<PostSite>( structuredClone( DEFAULT_POST_SITE ) );
  const [ postClient, setPostClient ] = useState<PostClient>( structuredClone( DEFAULT_POST_CLIENT ) );
  const [ selectedClient, setSelectedClient ] = useState<Option<Client> | undefined>();
  const [ selectedSite, setSelectedSite ] = useState<Option<Site> | undefined>();
  const [ selectedUsers, setSelectedUsers ] = useState<Option<UserOfAbilityLinked>[]>( [] );

  const [ allowedClients, setAllowedClients ] = useState<Option<Client>[]>( [] );
  const [ allowedSites, setAllowedSites ] = useState<Option<Site>[]>( [] );

  const [ rerenderClient, setRerenderClient ] = useState<number>( 0 );
  const [ rerenderSite, setRerenderSite ] = useState<number>( 0 );
  const [ rerenderUser, setRerenderUser ] = useState<number>( 0 );

  const [ canPost, setCanPost ] = useState<boolean>( false );
  const [ hasChange, setHasChange ] = useState<boolean>( false );
  const [ needCreateNewSite, setNeedCreateNewSite ] = useState<boolean>( false );
  const [ needCreateNewClient, setNeedCreateNewClient ] = useState<boolean>( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me && !( CommonGlobalService.isClientAdmin( me ) || CommonGlobalService.isSiteAdmin( me ) ) ) {
      NavigationService.toAbilities( navigate, false );
    }
    setIsLoading( true );
    initCountries();
    refreshClients();
  }, [] );

  useEffect( () => {
    if ( usePredefinedAuthorizations && selectedCountry ) {
      setIsLoading( true );
      loadAuthorizationsJson( selectedCountry.toLowerCase() ).then( authList => {
        setAllPredefinedAuthorizations( authList );
        const categories = Array.from( new Set( authList.map( a => {return a.category;} ).filter( Boolean ) ) );
        setAuthorizationCategories( categories );
        setSelectedAuthorizationCategory( '' );
      } ).finally( () => { setIsLoading( false ); } );
    } else {
      setAllPredefinedAuthorizations( [] );
      setAuthorizationCategories( [] );
      setSelectedAuthorizationCategory( '' );
    }
  }, [ usePredefinedAuthorizations, selectedCountry, i18n.language ] );

  useEffect( () => {
    let filtered = allPredefinedAuthorizations;
    if ( selectedAuthorizationCategory !== '' ) {
      filtered = filtered.filter( a => {return a.category === selectedAuthorizationCategory;} );
    }
    if ( selectedCountry ) {
      filtered = filtered.filter( a => {return !a.country || a.country === selectedCountry;} );
    }
    filtered = filtered.filter( a => {return !!a.name;} ).sort( ( a, b ) => {return a.name.localeCompare( b.name );} );
    setPredefinedAuthorizationsOptions( CommonGlobalService.toOptions( filtered ) );
  }, [ allPredefinedAuthorizations, selectedCountry, selectedAuthorizationCategory ] );

  useEffect( () => {
    checkCanPostClient();
  }, [ postAbility, postSite, postClient, selectedClient, selectedSite, usePredefinedAuthorizations, selectedPredefinedAuthorizations, selectedCountry ] );

  useEffect( () => {
    checkHasChangeClient();
  }, [ postAbility, postSite, postClient, selectedClient, selectedSite, selectedUsers, usePredefinedAuthorizations, selectedPredefinedAuthorizations, selectedCountry ] );

  useEffect( () => {
    setRerenderClient( rerenderClient + 1 );
  }, [ allowedClients ] );

  useEffect( () => {
    setRerenderSite( rerenderSite + 1 );
  }, [ allowedSites ] );

  useEffect( () => {
    if ( selectedClient != null ) {
      refreshSites( selectedClient.object.id );
    }
    postSite.clientId = selectedClient?.value || '';
    setNeedCreateNewClient( selectedClient?.object.id === createNewClient.id );
    setRerenderUser( rerenderUser + 1 );
    setRerenderSite( rerenderSite + 1 );
  }, [ selectedClient ] );

  useEffect( () => {
    setSelectedUsers( [] );
    postAbility.siteId = selectedSite?.value || '';
    setNeedCreateNewSite(  selectedClient != null && selectedSite?.object.id === createNewSite.id );
    setPostAbility( structuredClone( postAbility ) );
    if ( selectedSite?.object.id === createNewSite.id ) {
      setSelectedUsers( [] );
    }
  }, [ selectedSite ] );

  useEffect( () => {
    postAbility.users = selectedUsers.map( user => { return { id: user.object.id, expirationDate: user.object.expirationDate }; } );
    setPostAbility( structuredClone( postAbility ) );
    setRerenderUser( rerenderUser + 1 );
  }, [ selectedUsers ] );

  /*---------------------------- Functions ------------------------------*/
  const refreshClients = async (): Promise<void> => {
    setIsLoading( true );
    const response = await ClientService.getAllowedClients( null, token, dispatch );
    if ( response != null ) {
      if ( me?.isSuperAdmin ) {
        response.unshift( createNewClient );
      }
      const clients = CommonGlobalService.toOptions( response )
        .sort( client => { return client.object.id < createNewClient.id ? 1 : -1; } );
      setAllowedClients( clients );
      if ( clients.length === 1 ) {
        setSelectedClient( clients[ 0 ] );
      }
    }
    setTimeout(  () => { setIsLoading( false ); }, 75 );
  };

  const refreshSites = async ( clientId: string ): Promise<void> => {
    let response: Site[] = [];
    if ( clientId != null && clientId != '' && clientId != createNewClient.id ) {
      response = await SiteService.getAllowedSitesByClientsId( [ clientId ], token, dispatch );
    }
    const sitesAdmin = response.filter( site => {
      return ( me && ( CommonGlobalService.isAdminOfSite( me, site.id ) || CommonGlobalService.isAdminOfClient( me, site.clientId ) ) );
    } );
    if ( selectedSite?.object.id != clientId ) {
      setSelectedSite( undefined );
    }
    if ( sitesAdmin != null ) {
      if ( me?.isSuperAdmin ) {
        sitesAdmin.unshift( createNewSite );
      }

      const sites = CommonGlobalService.toOptions( sitesAdmin )
        .sort( site => { return site.object.id < createNewSite.id ? 1 : -1; } );
      setAllowedSites( sites );
      if ( sites.length === 1 ) {
        setSelectedSite( sites[ 0 ] );
      }
    }
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserOfAbilityLinked>[] }> => {
    const users: { total: number, list: Option<UserOfAbilityLinked>[] } = { total: 0, list: [] };
    if ( selectedSite && selectedSite.object.id !== createNewSite.id ) {
      if ( filter.filterName === undefined || filter.filterName === '' ) {
        delete filter.filterName;
      }
      if ( selectedSite != null ) {
        filter.filterSites = [ selectedSite.object.id ];
      }

      const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
      users.total = result.total;
      users.list = CommonGlobalService.toOptions<UserOfAbilityLinked>(
        result.users.map<UserOfAbilityLinked>( user => {
          return {
            id: user.id,
            fullname: user.fullname,
            isDisable: user.isDisable,
            isAdminOfUser: user.isAdminOfUser,
            expirationDate: moment().add( 1, 'month' ).startOf( 'day' ).toISOString(),
          };
        } )
      );
    }
    return users;
  };

  const resetForm = () => {
    setSelectedUsers( [] );
    setSelectedSite( allowedSites.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedSites[ 0 ] : undefined );
    setSelectedClient( allowedClients.length === ( me && me.isSuperAdmin ? 2 : 1 ) ? allowedClients[ 0 ] : undefined );
    // Toujours initialiser le postAbility avec les valeurs par défaut
    setPostAbility( structuredClone( DEFAULT_POST_ABILITY ) );
  };

  const checkCanPostClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
          ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postAbility.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
          ( postAbility.siteId != createNewSite.id && postAbility.siteId != null && postAbility.siteId != '' )
    );
    let can = false;
    if ( usePredefinedAuthorizations ) {
      can = clientChange && siteChange && selectedPredefinedAuthorizations.length > 0 && selectedCountry !== '';
    } else {
      const nameChange = postAbility.name != null && postAbility.name != '';

      can = clientChange && siteChange && nameChange;
    }
    setCanPost( can );
  };

  const checkHasChangeClient = () => {
    const clientChange = (
      ( selectedClient?.object.id == createNewClient.id && postClient.name != DEFAULT_POST_CLIENT.name ) ||
        ( selectedClient?.object.id != null && selectedClient?.object.id != createNewClient.id )
    );
    const siteChange = (
      ( postAbility.siteId == createNewSite.id && postSite.name != DEFAULT_POST_SITE.name ) ||
        ( postAbility.siteId != createNewSite.id && postAbility.siteId != null && postAbility.siteId != '' )
    );
    const nameChange = postAbility.name != null && postAbility.name != '';
    const usersChange = selectedUsers.length > 0;
    setHasChange( clientChange || siteChange || nameChange || usersChange );
  };

  const toPredefinedAuthorization = ( json: { data: { name: string; category: string }[] } ): PredefinedAuthorization[] => {
    if ( !json.data ) {
      return [];
    }

    return json.data.map( item => {
      const name = item.name;
      const category = item.category;

      return {
        id: `${category}-${name}`,
        name,
        category
      };
    } );
  };

  const loadAuthorizationsJson = async ( country: string ): Promise<PredefinedAuthorization[]> => {
    try {
      const res = await fetch( `/${jsonAuthorizationsDir}/${country}/${jsonAuthorizationsFilename}` );
      if ( res.ok ) {
        const json = await res.json();
        return toPredefinedAuthorization( json );
      }
    } catch ( error ) {
      logger.error( `Failed to fetch /${jsonAuthorizationsDir}/${country}/${jsonAuthorizationsFilename}`, error );
    }

    return [];
  };

  const initCountries = () => {
    const holidaysInstance = new Holidays();
    const countriesObj = holidaysInstance.getCountries( i18n.language );
    const countries = Object.entries( countriesObj ).map( function( entry ) {
      const code = entry[ 0 ];
      const data = entry[ 1 ] as { name?: string } | string;
      let label: string = '';
      if ( typeof data === 'string' ) {
        label = data;
      } else {
        label = data.name ? data.name : code;
      }
      return { label: label, value: code.toLowerCase() };
    } );
    setCountryOptions( countries );
    setSelectedCountry( '' );
  };

  /*------------------------------ Events -------------------------------*/
  const onCreateClick = async () => {
    setIsLoading( true );
    if ( postSite.clientId === createNewClient.id ) {
      const newClientId = await ClientService.postClient( postClient, token, dispatch );
      if ( newClientId ) {
        postSite.clientId = newClientId;
      }
    }
    if ( postAbility.siteId === createNewSite.id ) {
      const newSiteId = await SiteService.postSite( postSite, token, dispatch );
      if ( newSiteId ) {
        postAbility.siteId = newSiteId;
      }
    }
    if ( postAbility.siteId !== createNewSite.id ) {
      if ( usePredefinedAuthorizations ) {
        let createdCount = 0;
        for ( const auth of selectedPredefinedAuthorizations ) {
          const abilityToPost = {
            ...structuredClone( DEFAULT_POST_ABILITY ),
            ...postAbility,
            name: auth.label
          };
          const isCreated = await AbilityService.postAbility( abilityToPost, token, dispatch );
          if ( isCreated ) { createdCount++; }
        }
        if ( createdCount > 0 ) {
          toastService.showSuccess( t( 'All selected authorizations have been created.' ) );
        }
        resetForm();
        window.location.reload();
      } else {
        const abilityToPost = {
          ...structuredClone( DEFAULT_POST_ABILITY ),
          ...postAbility
        };
        const isCreated = await AbilityService.postAbility( abilityToPost, token, dispatch );
        if ( isCreated ) {
          toastService.showSuccess( t( 'The {0} authorization has been created.' ).replace( '{0}', ( postAbility.name ) ) );
          resetForm();
          window.location.reload();
        }
      }
    }
    setIsLoading( false );
  };


  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChange } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Create authorization' ) } needConfirmationToNav={ hasChange }/>
        <Breadcrumb
          needConfirmationToNav={ hasChange }
          links={
            [
              { label: t( 'common:authorization' ), route: ROUTES.ABILITIES, icon: ( <AbilitiesIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: t( 'Create new authorization' ), route: ROUTES.NEW_ABILITY, icon: ( <PlusIcon className='h-[12px] select-none cursor-pointer'/> ) }
            ]
          }
        />
        {
          !isLoading ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderClient }
                    disabled={ allowedClients.length === 1 }
                    value={ selectedClient }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ allowedClients }
                    onChange={ option => {
                      setSelectedClient( option );
                    } }
                  />
                  <div className='flex flex-col w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewClient ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_CLIENT_NAME }
                        label={ t( 'Client name' ) }
                        placeholder={ t( 'Client' ) + '...' }
                        icon={ <ClientsIcon className='w-7 h-7' /> }
                        value={ postClient.name }
                        onChange={ newClientName => {
                          postClient.name = newClientName;
                          setPostClient( structuredClone( postClient ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='flex flex-col w-full'>
                  <Select
                    required
                    key={ rerenderSite }
                    disabled={ !selectedClient || allowedSites.length === 1 }
                    value={ selectedSite }
                    label={ t( 'Site' ) }
                    placeholder={ t( 'No associated sites' ) }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    options={ allowedSites }
                    onChange={ option => {
                      setSelectedSite( option );
                    } }
                  />
                  <div className='w-full'>
                    <div className={`flex mx-3 border-x-2 border-b-2 rounded-b-md origin-top transition-all duration-500 ${needCreateNewSite ? 'mt-[2px] p-2 scale-100 opacity-100 max-h-96' : 'scale-y-50 opacity-0 max-h-0'}`}>
                      <Input
                        required
                        maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                        label={ t( 'Site name' ) }
                        placeholder={ t( 'Site' ) + '...' }
                        icon={ <SitesIcon className='w-7 h-7' /> }
                        value={ postSite.name }
                        onChange={ newSiteName => {
                          postSite.name = newSiteName;
                          setPostSite( structuredClone( postSite ) );
                        } }
                      />
                    </div>
                  </div>
                </div>

                <div className='w-full'>
                  {/* Checkbox pour activer le multi-select des authorizations pré-enregistrées */}
                  <div className='flex items-center mb-2'>
                    <input
                      type='checkbox'
                      id='predefined-authorizations-checkbox'
                      checked={usePredefinedAuthorizations}
                      onChange={e => {
                        setUsePredefinedAuthorizations( e.target.checked );
                        if ( !e.target.checked ) {
                          setSelectedPredefinedAuthorizations( [] );
                          setSelectedCountry( '' );
                        }
                      }}
                      className='mr-2 h-4 w-4 cursor-pointer'
                    />
                    <label htmlFor='predefined-authorizations-checkbox' className='cursor-pointer select-none'>
                      {t( 'add_predefined_authorizations' )}
                    </label>
                  </div>
                  {/* Filtre par catégorie, pays et multi-select */}
                  {usePredefinedAuthorizations ? (
                    <>
                      <div className='mb-2'>
                        <Select
                          label={t( 'country' )}
                          required={true}
                          placeholder={t( 'country_placeholder' )}
                          options={countryOptions}
                          value={
                            selectedCountry !== ''
                              ? countryOptions.find( opt => {return opt.value === selectedCountry;} )
                              : undefined
                          }
                          onChange={option => { setSelectedCountry( option.value ); }}
                        />
                      </div>
                      <div className='mb-2'>
                        <Select
                          label={t( 'category' )}
                          required={false}
                          disabled={!selectedCountry}
                          options={[ { label: t( 'common:all' ), value: '' }, ...authorizationCategories.map( cat => {return { label: cat, value: cat };} ) ]}
                          value={selectedAuthorizationCategory !== '' ? { label: selectedAuthorizationCategory, value: selectedAuthorizationCategory } : { label: t( 'common:all' ), value: '' }}
                          onChange={option => { setSelectedAuthorizationCategory( option.value ); }}
                        />
                      </div>
                      <MultiSelect
                        label={ t( 'translation:predefined_authorization' ) }
                        placeholder={ t( 'translation:select_authorizations' ) }
                        icon={ <AbilitiesIcon className='w-7 h-7' /> }
                        getValues={ predefinedAuthorizationsOptions }
                        selectedValues={ selectedPredefinedAuthorizations }
                        onChange={ setSelectedPredefinedAuthorizations }
                        required
                        disabled={ !selectedCountry }
                      />
                    </>
                  ) : (
                    <Input
                      required
                      maxLength={MAX_LENGTH_ENTITY.UF_ABILITY_NAME}
                      label={t( 'authorization_name' )}
                      placeholder={t( 'authorization' ) + '...'}
                      icon={<AbilitiesIcon className='w-7 h-7' />}
                      value={postAbility.name}
                      onChange={newAbilityName => {
                        postAbility.name = newAbilityName;
                        setPostAbility( structuredClone( postAbility ) );
                      }}
                    />
                  )}
                </div>

                <div className='w-1/4 min-w-[225px] self-start'>
                  <ColorPicker
                    label={ usePredefinedAuthorizations ? t( 'translation:predefined_authorization_color' ) : t( 'translation:authorization_color' ) }
                    value={ postAbility.color }
                    onBlur={ newAbilityColor => {
                      postAbility.color = newAbilityColor;
                      setPostAbility( structuredClone( postAbility ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    key={ selectedSite?.value }
                    disabled={ !selectedSite || selectedSite.object.id === createNewSite.id }
                    label={ t( 'User' ) }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedUsers }
                    getValues={ getUsers }
                    onChange={ options => {
                      setSelectedUsers( options );
                    } }
                    extra={ user => {
                      return (
                        <ExpirationAbility
                          ability={ {
                            id: user.object.id,
                            siteId: postAbility.siteId,
                            name: postAbility.name,
                            color: postAbility.color,
                            expirationDate: user.object.expirationDate
                          } }
                          onChange={ changedAbility => {
                            user.object.expirationDate = changedAbility.expirationDate;
                            const findedUser = selectedUsers.find( item => { return changedAbility.id == item.object.id;  } );
                            if ( findedUser ) {
                              findedUser.object.expirationDate = changedAbility.expirationDate;
                            }
                            setSelectedUsers( structuredClone( selectedUsers ) );
                          } }
                        />
                      );
                    } }
                  />
                </div>

                <div className='flex h-fit w-1/3 min-w-[160px] max-w-[250px] justify-center mt-10'>
                  <Button
                    label={ t( 'Create' ) }
                    disable={ !canPost }
                    icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                    color='#0bb173'
                    colorHover='#088D5C'
                    onClick={ onCreateClick }
                  />
                </div>
              </form>
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}